import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, X, ImagePlus, FileUp, CheckCircle2 } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { ADMIN_MENU } from "../../lib/menus";
import { PRODUCT_TYPES, mediaUrl } from "../../lib/format";
import api, { apiError } from "../../lib/api";

const EMPTY = {
  title: "", slug: "", short_description: "", description: "", category_id: "", product_type: "APPLICATION",
  price: 0, thumbnail: "", gallery: [], technology: [], version: "1.0.0", file_size: "",
  application_file: null, source_code_file: null, documentation_file: null, demo_url: "",
  features: [], changelog: "", is_new: false, is_popular: false, status: "PUBLISHED",
};

export default function ProductForm() {
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    document.title = (isEdit ? "Edit" : "Tambah") + " Produk - DS KODE";
    api.get("/categories?all=true").then((r) => setCats(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/products/${slug}`).then((r) => {
        const d = r.data;
        setForm({ ...EMPTY, ...d });
        setLoading(false);
      }).catch(() => { toast.error("Produk tidak ditemukan"); navigate("/admin/products"); });
    }
  }, [isEdit, slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const trackProgress = (key) => ({
    onUploadProgress: (event) => {
      const percent = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
      setUploadProgress((prev) => ({ ...prev, [key]: percent }));
    },
  });

  const uploadImage = async (e, target) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", "image");
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, ...trackProgress(target) });
      if (target === "thumbnail") set("thumbnail", data.file_id);
      else set("gallery", [...form.gallery, data.file_id]);
      toast.success("Gambar diunggah");
    } catch (err) { toast.error(apiError(err)); } finally {
      setUploadProgress((prev) => ({ ...prev, [target]: undefined }));
    }
  };

  const uploadFile = async (e, kind, field) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, ...trackProgress(field) });
      set(field, data.file_id);
      toast.success(`${file.name} diunggah`);
    } catch (err) { toast.error(apiError(err)); } finally {
      setUploadProgress((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const deleteFile = async (field) => {
    const fileId = form[field];
    if (!fileId || !window.confirm("Hapus file ini? File akan dihapus permanen dari storage.")) return;
    try {
      await api.delete(`/products/${form.id}/files/${fileId}`);
      set(field, field === "gallery" ? form.gallery.filter((item) => item !== fileId) : null);
      toast.success("File dihapus");
    } catch (err) { toast.error(apiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, price: Number(form.price) || 0 };
    try {
      if (isEdit) { await api.put(`/products/${form.id}`, payload); toast.success("Produk diperbarui"); }
      else { await api.post("/products", payload); toast.success("Produk berhasil dibuat"); }
      navigate("/admin/products");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (loading) return <DashboardLayout title="Edit Produk" menu={ADMIN_MENU}><div className="h-96 skeleton rounded-xl" /></DashboardLayout>;

  return (
    <DashboardLayout title={isEdit ? "Edit Produk" : "Tambah Produk"} menu={ADMIN_MENU} testid="product-form">
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl">
        <div className="lg:col-span-2 space-y-5">
          <div className="card p-5 space-y-4">
            <h3 className="font-heading font-bold text-slate-900">Informasi Produk</h3>
            <Row label="Nama Produk"><input required value={form.title} onChange={(e) => set("title", e.target.value)} className="input" data-testid="pf-title" /></Row>
            <Row label="Slug (opsional)"><input value={form.slug} onChange={(e) => set("slug", e.target.value)} className="input" placeholder="otomatis dari nama" data-testid="pf-slug" /></Row>
            <div className="grid grid-cols-2 gap-4">
              <Row label="Kategori"><select value={form.category_id || ""} onChange={(e) => set("category_id", e.target.value)} className="input" data-testid="pf-category"><option value="">Pilih kategori</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Row>
              <Row label="Jenis Produk"><select value={form.product_type} onChange={(e) => set("product_type", e.target.value)} className="input" data-testid="pf-type">{PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></Row>
            </div>
            <Row label="Deskripsi Singkat"><input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="input" data-testid="pf-short" /></Row>
            <Row label="Deskripsi Lengkap"><textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="input" data-testid="pf-desc" /></Row>
            <div className="grid grid-cols-2 gap-4">
              <Row label="Harga (Rp, 0 = gratis)"><input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="input" data-testid="pf-price" /></Row>
              <Row label="Versi"><input value={form.version} onChange={(e) => set("version", e.target.value)} className="input" /></Row>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Row label="Ukuran File"><input value={form.file_size} onChange={(e) => set("file_size", e.target.value)} className="input" placeholder="12 MB" /></Row>
              <Row label="Link Download (opsional)"><input type="url" value={form.download_url || ""} onChange={(e) => set("download_url", e.target.value)} className="input" placeholder="https://..." /></Row>
              <Row label="Demo URL"><input value={form.demo_url} onChange={(e) => set("demo_url", e.target.value)} className="input" /></Row>
            </div>
            <Row label="Teknologi (pisah dengan koma)"><input value={form.technology.join(", ")} onChange={(e) => set("technology", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="input" placeholder="React, Node.js" data-testid="pf-tech" /></Row>
            <Row label="Fitur (satu per baris)"><textarea rows={4} value={form.features.join("\n")} onChange={(e) => set("features", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="input" /></Row>
            <Row label="Changelog"><textarea rows={3} value={form.changelog} onChange={(e) => set("changelog", e.target.value)} className="input" /></Row>
          </div>

          <div className="card p-5 space-y-4">
            <h3 className="font-heading font-bold text-slate-900">File Produk (tersproteksi)</h3>
            <FileField label="File Aplikasi" fileId={form.application_file} progress={uploadProgress.application_file} onChange={(e) => uploadFile(e, "application", "application_file")} onDelete={() => deleteFile("application_file")} testid="pf-app-file" />
            <FileField label="File Source Code" fileId={form.source_code_file} progress={uploadProgress.source_code_file} onChange={(e) => uploadFile(e, "source_code", "source_code_file")} onDelete={() => deleteFile("source_code_file")} testid="pf-src-file" />
            <FileField label="File Dokumentasi" fileId={form.documentation_file} progress={uploadProgress.documentation_file} onChange={(e) => uploadFile(e, "documentation", "documentation_file")} onDelete={() => deleteFile("documentation_file")} testid="pf-doc-file" />
          </div>
        </div>

        <div className="space-y-5">
          <div className="card p-5 space-y-4">
            <h3 className="font-heading font-bold text-slate-900">Status</h3>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="input" data-testid="pf-status"><option value="PUBLISHED">Terbit</option><option value="DRAFT">Draft</option><option value="ARCHIVED">Arsip</option></select>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.is_new} onChange={(e) => set("is_new", e.target.checked)} className="rounded" /> Badge NEW</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.is_popular} onChange={(e) => set("is_popular", e.target.checked)} className="rounded" /> Badge POPULER</label>
          </div>

          <div className="card p-5 space-y-3">
            <h3 className="font-heading font-bold text-slate-900">Thumbnail</h3>
            {form.thumbnail ? (
              <div className="relative"><img src={mediaUrl(form.thumbnail)} alt="" className="w-full aspect-video object-cover rounded-lg" /><button type="button" onClick={() => set("thumbnail", "")} className="absolute top-2 right-2 p-1.5 rounded-full bg-slate-900/70 text-white"><X className="h-4 w-4" /></button></div>
            ) : (
              <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-indigo-300 text-slate-400" data-testid="pf-thumbnail">
                {uploadProgress.thumbnail != null ? (
                  <>
                    <Loader2 className="h-6 w-6 mb-1 animate-spin text-indigo-500" />
                    <span className="text-xs text-indigo-600 font-medium">Mengunggah... {uploadProgress.thumbnail}%</span>
                  </>
                ) : (
                  <>
                    <ImagePlus className="h-8 w-8 mb-1" /><span className="text-xs">Unggah Thumbnail</span>
                  </>
                )}
                <input type="file" accept="image/*" className="hidden" disabled={uploadProgress.thumbnail != null} onChange={(e) => uploadImage(e, "thumbnail")} />
              </label>
            )}
          </div>

          <div className="card p-5 space-y-3">
            <h3 className="font-heading font-bold text-slate-900">Galeri</h3>
            <div className="grid grid-cols-3 gap-2">
              {form.gallery.map((g, i) => (
                <div key={i} className="relative"><img src={mediaUrl(g)} alt="" className="aspect-square object-cover rounded-lg" /><button type="button" onClick={() => set("gallery", form.gallery.filter((_, x) => x !== i))} className="absolute top-1 right-1 p-1 rounded-full bg-slate-900/70 text-white"><X className="h-3 w-3" /></button></div>
              ))}
              <label className="flex items-center justify-center aspect-square border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-indigo-300 text-slate-400"><Upload className="h-5 w-5" /><input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e, "gallery")} /></label>
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="pf-submit" className="btn-primary w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Simpan Perubahan" : "Buat Produk"}</button>
        </div>
      </form>
    </DashboardLayout>
  );
}

function Row({ label, children }) {
  return <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>;
}
function FileField({ label, fileId, progress, onChange, onDelete, testid }) {
  const uploading = progress != null;
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
      <label className="flex flex-1 items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50 text-sm" data-testid={testid}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin text-indigo-500" /> : fileId ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <FileUp className="h-4 w-4 text-slate-400" />}
        <span className={uploading ? "text-indigo-600 font-medium" : fileId ? "text-emerald-600 font-medium" : "text-slate-400"}>
          {uploading ? `Mengunggah... ${progress}%` : fileId ? "File terunggah — klik untuk ganti" : "Pilih file (zip, rar, pdf)"}
        </span>
        <input type="file" accept=".zip,.rar,.pdf,.txt,.json" className="hidden" disabled={uploading} onChange={onChange} />
      </label>
      {fileId && !uploading && <button type="button" onClick={onDelete} data-testid={`delete-${testid}`} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-2 text-xs font-semibold text-red-600 hover:bg-red-50" aria-label={`Hapus ${label}`}><X className="h-3.5 w-3.5" /> Hapus</button>}
      </div>
      {uploading && (
        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-indigo-600 transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}
